import apiClient from "../axios";
import { PROJECT_KEY } from "@/src/services/blocks/config";
import { NotificationItem } from "@/src/models/notification.model";
import { GetNotificationsParams } from "./notification.hooks";
import {
  fetchInboxRows,
  INBOX_ID_PREFIX,
  insertInboxRows,
  markInboxRowRead,
  mergeInboxPage,
} from "./notification-inbox.service";

/**
 * Notifier lives on the `logic` v4 service.
 *
 * NOT `utilities` — that service publishes no Notifier controller at all, but it
 * answers bare 401s (empty body) for paths under it, which reads like "route exists"
 * if you only look at the status code. Verified 2026-08-05 against swagger on both
 * api.seliseblocks.com and blocksapi.slsblx.com: logic 5 Notifier paths, utilities 0.
 */
const NOTIFIER_BASE = `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/logic/v4/Notifier`;

/**
 * Also `logic` v4, sibling to Notifier above.
 *
 * `Mail/Send` only resolves Blocks' built-in system purposes (AccountActivated,
 * VerifyEmail, RecoverAccount, MfaViaEmail, AccountActivation, project_invitation —
 * all `generatedBy: "Tenant"`). A custom Portal-authored template — e.g. the
 * tenant's own "NewsCreated" — 400s there: "The purpose and language combination
 * does not exist. Please check the EmailTemplates collection". `Mail/SendToAny`
 * resolves custom templates too; verified live 2026-08-31 with a real send. Use
 * `SendToAny`, not `Send`, for any project-authored template.
 *
 * This is independent of `Notifier/Notify`'s configuration-name plane — no
 * `Notification/Save` config is needed for this path.
 */
const MAIL_BASE = `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/logic/v4/Mail`;

/**
 * The bell's data: the legacy Notifier inbox merged with `NotificationInbox`
 * (see notification-inbox.service.ts), deduped. The response keeps the Notifier
 * shape so the list and badge need no changes; `unReadNotificationsCount` is the
 * merged count when the page is unread-only. An inbox failure degrades to the
 * Notifier page alone.
 */
export const getNotifications = async (params: GetNotificationsParams) => {
  const [legacy, inbox] = await Promise.all([
    apiClient
      .get(`${NOTIFIER_BASE}/GetNotifications`, { params })
      .then((res) => res.data),
    fetchInboxRows(
      (params.Page ?? 0) + 1,
      params.PageSize,
      !!params.IsUnreadOnly,
    ).catch((error) => {
      console.error("getNotifications: NotificationInbox read failed", error);
      return null;
    }),
  ]);
  if (!inbox) return legacy;

  const { items, duplicates } = mergeInboxPage(
    (legacy?.notifications ?? []) as NotificationItem[],
    inbox.items,
  );
  return {
    ...legacy,
    notifications: items,
    unReadNotificationsCount: params.IsUnreadOnly
      ? Math.max(
          0,
          (legacy?.unReadNotificationsCount ?? 0) + inbox.totalCount - duplicates,
        )
      : legacy?.unReadNotificationsCount,
  };
};

/**
 * Ids are a Notifier id, `inbox:<itemId>`, or both joined with `|` for a
 * deduped pair — every part is marked read in its own store.
 */
export const markNotificationAsRead = async (id: string) => {
  const parts = id.split("|").filter(Boolean);
  let legacyResult: unknown = null;
  for (const part of parts) {
    if (part.startsWith(INBOX_ID_PREFIX)) {
      await markInboxRowRead(part.slice(INBOX_ID_PREFIX.length));
    } else {
      const res = await apiClient.post(
        `${NOTIFIER_BASE}/MarkNotificationAsRead`,
        { id: part },
      );
      legacyResult = res.data;
    }
  }
  return legacyResult;
};

const MARK_ALL_INBOX_PAGE = 100;

export const markAllNotificationAsRead = async () => {
  const res = await apiClient.post(
    `${NOTIFIER_BASE}/MarkAllNotificationAsRead`,
    {},
  );
  try {
    // Marking rows read removes them from the unread query, so page 1 is
    // re-read until it is empty (bounded).
    for (let i = 0; i < 10; i++) {
      const { items } = await fetchInboxRows(1, MARK_ALL_INBOX_PAGE, true);
      if (!items.length) break;
      await Promise.all(items.map((row) => markInboxRowRead(row.ItemId)));
    }
  } catch (error) {
    console.error("markAllNotificationAsRead: NotificationInbox failed", error);
  }
  return res.data;
};

export const NOTIFICATIONS_ENABLED = true;

/**
 * The CONFIG plane — tenant-wide notification definitions, a sibling of the inbox
 * plane above rather than part of it.
 *
 * ⚠ Only `Gets` is in scope. Never call `DELETE .../Notification/Delete`: it removes
 * a tenant-wide definition and breaks delivery for every user in the project, and it
 * returns 200 while doing it. The controller also ships on `os/v4` — same trap there.
 *
 * A definition carries both vocabularies this feature uses, and they are NOT the same
 * string:
 *   - `name`         — kebab-case, what `Notify` takes as `configurationName`
 *   - `notifyMethod` — the SignalR event name to subscribe to
 */
const NOTIFICATION_CONFIG_BASE = `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/logic/v4/Notification`;

export type NotificationConfiguration = {
  itemId?: string;
  name?: string;
  notifyMethod?: string;
  notificationType?: number;
  channelToNotify?: number;
  enablePersistence?: boolean;
};

export type GetNotificationConfigurationsResponse = {
  configurations?: NotificationConfiguration[];
  isSuccess?: boolean;
  totalCount?: number;
  errors?: Record<string, string>;
};

export const getNotificationConfigurations = async (): Promise<
  GetNotificationConfigurationsResponse
> => {
  const res = await apiClient.get<GetNotificationConfigurationsResponse>(
    `${NOTIFICATION_CONFIG_BASE}/Gets`,
    { params: { Page: 0, PageSize: 100 } },
  );
  return res.data;
};

/**
 * `Notify` requires ALL of these. `responseKey` and `responseValue` read like
 * metadata but the server rejects the request without them:
 *
 *   POST /logic/v4/Notifier/Notify {}
 *   → 400 required: Roles, UserIds, ResponseKey, ConnectionId,
 *                   ResponseValue, ConfigurationName, DenormalizedPayload
 *
 * `configurationName` is spelled correctly. An earlier version of this file spelled
 * it "configuratoinName" (transposed), mirroring a typo on the dead v1 service. That
 * key does not exist on logic/v4: the server then sees ConfigurationName as missing
 * and rejects the whole request with a 400. The spelling is pinned by a test — do
 * not "fix" it in either direction without re-reading the live swagger.
 */
export type SendNotificationPayload = {
  userIds: string[];
  configurationName: string;
  denormalizedPayload: string;
  responseKey: string;
  responseValue: string;
  roles?: string[];
  connectionId?: string;
};

/**
 * A Notify call can fail in three ways, and only one of them throws:
 *
 *   1. transport/HTTP error      → axios rejects
 *   2. model validation (400)    → axios rejects
 *   3. business rejection        → **HTTP 200** with {isSuccess: false}
 *
 * (3) is the one that hides. An unknown `configurationName` returns
 * `200 {"errors":{"ConfigurationName":"no_configuration_exist"},"isSuccess":false}`
 * — axios resolves, `res.data` is truthy, and the caller counts it as delivered.
 *
 * This function resolves to `null` for every failure rather than rejecting, because
 * it is `await`ed inside the news and wiki create/edit mutations *after* the record
 * is written: a rejection there made the whole mutation report failure on an article
 * that had actually saved. Handling it centrally keeps all call sites safe without
 * editing each one. Callers that care can check for `null`.
 *
 * Failures are logged loudly — swallowing them silently is what left this feature
 * broken app-wide without anyone noticing.
 */
export const sendNotification = async (payload: SendNotificationPayload) => {
  if (!NOTIFICATIONS_ENABLED) return null;

  // Mobile push comes from NotificationInbox rows, not from the Notifier, so
  // write them alongside (best effort, never rejects).
  const inboxWrite = insertInboxRows(
    payload.userIds,
    payload.denormalizedPayload,
  );

  try {
    const res = await apiClient.post(`${NOTIFIER_BASE}/Notify`, {
      connectionId: "",
      roles: [""],
      ...payload,
    });

    if (res.data?.isSuccess === false) {
      console.error(
        "sendNotification: server rejected the notification (HTTP 200)",
        {
          configurationName: payload.configurationName,
          errors: res.data?.errors,
        },
      );
      await inboxWrite;
      return null;
    }

    await inboxWrite;
    return res.data;
  } catch (error) {
    console.error("sendNotification: request failed", {
      configurationName: payload.configurationName,
      error,
    });
    await inboxWrite;
    return null;
  }
};

/**
 * `purpose` must name a template `generatedBy` a real user (a Portal-authored
 * template) — see the `MAIL_BASE` note above for why this goes through
 * `SendToAny` rather than `Send`.
 *
 * `bodyDataContext`/`subjectDataContext` are the template's merge fields
 * (`{{DisplayName}}`, `{{HostName}}`, …) — whatever keys the template in the
 * Portal's Mail Template Builder actually references.
 */
export type SendMailPayload = {
  purpose: string;
  to?: string[];
  bcc?: string[];
  cc?: string[];
  language?: string;
  bodyDataContext?: Record<string, string>;
  subjectDataContext?: Record<string, string>;
};

/**
 * Same resolve-rather-than-reject contract as `sendNotification`: this is
 * awaited after the record it announces has already been saved, so a mail
 * failure must never surface as a create/edit failure. Failures are logged
 * loudly rather than swallowed — see the comment on `sendNotification` for why
 * that matters on this platform (a well-formed request can still come back
 * `isSuccess: false` on HTTP 200).
 */
export const sendMail = async (payload: SendMailPayload) => {
  try {
    const res = await apiClient.post(`${MAIL_BASE}/SendToAny`, {
      to: [],
      bcc: [],
      cc: [],
      language: "en-US",
      sendPhoneNumberAsEmail: false,
      projectKey: PROJECT_KEY,
      ...payload,
    });

    if (res.data?.isSuccess === false) {
      console.error("sendMail: server rejected the email (HTTP 200)", {
        purpose: payload.purpose,
        errors: res.data?.errors,
      });
      return null;
    }

    return res.data;
  } catch (error) {
    console.error("sendMail: request failed", {
      purpose: payload.purpose,
      error,
    });
    return null;
  }
};
